import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Library as LibraryIcon, Loader2, BookOpenText, Sparkles, Search, Wand2 } from "lucide-react";
import { UploadDialog } from "@/components/library/UploadDialog";
import { DocumentCard } from "@/components/library/DocumentCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { docsApi } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/context/LanguageContext";
import { toast } from "sonner";

const SAMPLE_TITLE = "How to Read 3x Faster (and Remember It)";
const SAMPLE_TEXT = `How to Read 3x Faster (and Remember It)

The average adult reads at about 230 words per minute. That number has barely changed since school, and for one simple reason: almost nobody is ever taught how to read. We are taught what the letters mean, and then left to our own habits for the rest of our lives. The good news is that reading is a skill, and like any skill it can be trained. With a little understanding of how the eye and brain actually process text, most people can comfortably double or triple their speed while keeping, and often improving, their comprehension.

The first thing to understand is subvocalization. When you read, a small voice in your head pronounces each word. This inner speech is comforting and familiar, but it chains your reading speed to your speaking speed, which tops out around 200 to 300 words per minute. You do not actually need to hear a word to understand it. You already recognize thousands of words instantly by their shape, the way you recognize a friend's face without sounding out their name. Training yourself to trust that instant recognition is the single biggest unlock in reading faster.

The second idea is the fixation. Your eyes do not glide smoothly across a line of text. They jump, pause, and jump again, landing on a series of fixation points. At each pause, your brain captures a small cluster of words. Slow readers make many short fixations and frequently jump backward to reread, a habit called regression. Fast readers make fewer, more confident fixations and rarely look back. One powerful way to train this is to remove the wandering entirely: instead of moving your eyes across static text, let the text come to you, one focal point at a time. This is the principle behind rapid serial visual presentation, where words flash at a fixed point so your eyes never have to search.

The third idea is the optimal recognition point. Every word has a spot, usually just left of center, where the eye most efficiently identifies it. When that point is highlighted and held in a consistent place, your eyes stop drifting and your brain locks on faster. Aligning each word to its recognition point can meaningfully reduce eye movement and fatigue over a long reading session.

Speed without comprehension is useless, so retention deserves equal attention. Three habits help enormously. First, warm up: start a fraction slower and let your speed ramp up over the first few seconds, the same way a sprinter does not begin at full pace. Second, read in meaningful chunks rather than isolated words when the material is dense, because meaning lives in phrases, not in single tokens. Third, and most important, practice active recall. Reading is input; remembering is output. After a chapter, look away and try to reconstruct the main idea in your own words. A single minute of recall beats ten minutes of rereading.

Finally, protect consistency. Reading speed, like compound interest, rewards small daily practice far more than occasional marathons. Twenty focused minutes a day will, over a month, retrain habits that took years to form. Track your words per minute, celebrate small wins, and never break the streak two days in a row.

Put these together and the path is clear: quiet the inner voice, steady your eyes on a single point, ramp up gently, chunk dense material, and recall what you read. Do that consistently and three times your old speed is not a fantasy. It is simply what reading looks like once you have actually been taught how.`;

export default function Library() {
    const { user } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [sort, setSort] = useState("recent");
    const [sampleLoading, setSampleLoading] = useState(false);

    const load = useCallback(async () => {
        try {
            const list = await docsApi.list();
            setDocs(list);
        } catch (e) {
            toast.error(t("library.loadFailed"));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    // While any document is still parsing in the background, poll the library
    // so it auto-updates from "processing" to "ready" (or "failed").
    useEffect(() => {
        const hasProcessing = docs.some((d) => d.status === "processing");
        if (!hasProcessing) return;
        const interval = setInterval(async () => {
            try {
                const list = await docsApi.list();
                setDocs((prev) => {
                    list.forEach((nd) => {
                        const old = prev.find((d) => d.id === nd.id);
                        if (old?.status === "processing" && nd.status === "ready") {
                            toast.success(t("library.processingReady", { title: nd.title }));
                        } else if (old?.status === "processing" && nd.status === "failed") {
                            toast.error(nd.error || t("library.processingFailed"));
                        }
                    });
                    return list;
                });
            } catch (e) { /* keep polling */ }
        }, 2500);
        return () => clearInterval(interval);
    }, [docs, t]);

    const onUploaded = (doc) => setDocs((d) => [doc, ...d]);
    const onDelete = async (id) => {
        setDocs((d) => d.filter((x) => x.id !== id));
        try { await docsApi.remove(id); toast.success(t("library.deleted")); }
        catch (e) { toast.error(t("library.deleteFailed")); load(); }
    };

    const onRetry = async (id) => {
        try {
            const updated = await docsApi.retry(id);
            setDocs((d) => d.map((x) => (x.id === id ? { ...x, ...updated } : x)));
            toast.info(t("card.retrying"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("library.processingFailed"));
        }
    };

    const loadSample = async () => {
        setSampleLoading(true);
        try {
            const doc = await docsApi.uploadText(SAMPLE_TITLE, SAMPLE_TEXT);
            toast.success(t("library.sampleAdded"));
            navigate(`/read/${doc.id}`);
        } catch (e) {
            toast.error(t("library.sampleFailed"));
        } finally {
            setSampleLoading(false);
        }
    };

    const inProgress = useMemo(
        () => docs.filter((d) => (d.progress?.percent || 0) > 0 && !d.progress?.completed),
        [docs]
    );

    const visible = useMemo(() => {
        let list = docs;
        if (query.trim()) {
            const q = query.toLowerCase();
            list = list.filter((d) => (d.title || "").toLowerCase().includes(q));
        }
        const sorted = [...list];
        if (sort === "recent") sorted.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        else if (sort === "title") sorted.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
        else if (sort === "progress") sorted.sort((a, b) => (b.progress?.percent || 0) - (a.progress?.percent || 0));
        else if (sort === "length") sorted.sort((a, b) => (b.word_count || 0) - (a.word_count || 0));
        return sorted;
    }, [docs, query, sort]);

    const hour = new Date().getHours();
    const greeting = hour < 12 ? t("library.morning") : hour < 18 ? t("library.afternoon") : t("library.evening");

    return (
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:py-10">
            <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                <div>
                    <h1 className="font-sans-display text-2xl font-semibold tracking-tight text-t1 sm:text-3xl">
                        {greeting}, {user?.name?.split(" ")[0]}
                    </h1>
                    <p className="mt-1.5 text-t3">{t("library.subtitle", { count: docs.length, items: docs.length === 1 ? t("library.itemOne") : t("library.itemOther") })}</p>
                </div>
                <UploadDialog onUploaded={onUploaded} />
            </div>

            {/* Continue reading */}
            {inProgress.length > 0 && (
                <div className="mt-8">
                    <div className="mb-3 flex items-center gap-2">
                        <BookOpenText className="h-4 w-4 text-[hsl(var(--primary))]" />
                        <h2 className="font-sans-display text-sm font-semibold text-t2">{t("library.continueReading")}</h2>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {inProgress.slice(0, 3).map((doc, i) => <DocumentCard key={doc.id} doc={doc} index={i} onDelete={onDelete} onRetry={onRetry} />)}
                    </div>
                </div>
            )}

            {/* All */}
            <div className="mt-8">
                <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2">
                        <LibraryIcon className="h-4 w-4 text-[hsl(var(--primary))]" />
                        <h2 className="font-sans-display text-sm font-semibold text-t2">{t("library.allDocuments")}</h2>
                    </div>
                    {docs.length > 0 && (
                        <div className="flex items-center gap-2">
                            <div className="relative">
                                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-t4" />
                                <Input
                                    data-testid="library-search-input"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder={t("library.search")}
                                    className="h-9 w-44 bg-surface-2 pl-8 text-sm"
                                />
                            </div>
                            <Select value={sort} onValueChange={setSort}>
                                <SelectTrigger data-testid="library-sort-select" className="h-9 w-[140px] bg-surface-2 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="recent">{t("library.sortRecent")}</SelectItem>
                                    <SelectItem value="title">{t("library.sortTitle")}</SelectItem>
                                    <SelectItem value="progress">{t("library.sortProgress")}</SelectItem>
                                    <SelectItem value="length">{t("library.sortLength")}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>
                ) : docs.length === 0 ? (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-border bg-surface-1 px-6 py-16 text-center">
                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-2 text-[hsl(var(--primary))]"><Sparkles className="h-7 w-7" /></div>
                        <h3 className="mt-4 font-sans-display text-lg font-semibold text-t1">{t("library.emptyTitle")}</h3>
                        <p className="mt-1.5 max-w-sm text-sm text-t3">{t("library.emptyBody")}</p>
                        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                            <Button data-testid="library-sample-button" variant="secondary" className="gap-2 bg-surface-3/60" disabled={sampleLoading} onClick={loadSample}>
                                {sampleLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} {t("library.trySample")}
                            </Button>
                            <UploadDialog onUploaded={onUploaded} />
                        </div>
                    </motion.div>
                ) : visible.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-border bg-surface-1 px-6 py-12 text-center text-sm text-t3">{t("library.noMatch", { query })}</div>
                ) : (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {visible.map((doc, i) => <DocumentCard key={doc.id} doc={doc} index={i} onDelete={onDelete} onRetry={onRetry} />)}
                    </div>
                )}
            </div>
        </div>
    );
}
